/**
 * FIELD-RULES ENGINE
 *
 * Merges two sources at render time:
 *   1. FORM_SECTIONS (lib/formSchema.js) — structure and conditional reveal.
 *   2. settings/fieldRules in Firestore  — per-path requirement + help text.
 *
 * Nothing about "is this field required" is hardcoded in the components; an admin
 * changes `settings/fieldRules` in the Admin panel and the form reacts on next load.
 *
 * Three requirement levels:
 *   required      → blocks submission
 *   soft_required → blocks only until the submitter either fills it in or gives a
 *                   reason. The reasons travel to `submitMatter` as `softRequiredBypasses`
 *                   and are written to the matter audit log.
 *   optional      → no validation
 *
 * A hidden field is never validated: if `visibleIf` is false the field does not exist
 * for the user, so requiring it would deadlock the form.
 */

import { evaluateCondition, getPath } from './conditions.js';
import { originationCapPercent as firmOriginationCap, originationProblems } from './origination.js';
import { isAcceptedBypassReason } from './bypassReason.js';
import { addressProblem } from './address.js';
import { ALL_FIELDS, FORM_SECTIONS } from './formSchema.js';
import { percentParams } from './fieldLayout.js';

export const REQUIREMENT = {
  REQUIRED: 'required',
  SOFT_REQUIRED: 'soft_required',
  OPTIONAL: 'optional',
};

const DEFAULT_REQUIREMENT = REQUIREMENT.OPTIONAL;

/** True when a value counts as "not filled in" for validation purposes. */
export function isBlank(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  // An array of entirely-empty entries is still "not filled in": clicking "+ Add" on a
  // repeating group must not satisfy a required field by itself. (Recursing also covers
  // an array of blank strings.)
  if (Array.isArray(value)) return value.length === 0 || value.every(isBlank);
  if (typeof value === 'object') return Object.values(value).every(isBlank);
  return false;
}

export function isFieldVisible(field, matter) {
  return evaluateCondition(field.visibleIf, matter);
}

/**
 * The per-field starting values an admin has set, as [path, value] pairs.
 *
 * A default is a literal — string, boolean or number — exactly as an auto-fill rule's `value` is.
 * Anything else is ignored rather than written into a matter, because a default is a starting
 * answer, not a computation; the computed cases are what settings/fieldDerivations is for.
 *
 * An empty string is NOT a default. It is what the field already holds, so treating it as one
 * would make "clear this box" indistinguishable from "no default set" in the admin panel.
 */
export function isUsableDefault(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value !== '';
  return typeof value === 'boolean' || typeof value === 'number';
}

export function fieldDefaultEntries(rulesDoc) {
  const rules = (rulesDoc && rulesDoc.rules) || {};
  return Object.entries(rules)
    .map(([path, rule]) => [path, rule?.defaultValue])
    .filter(([, value]) => isUsableDefault(value));
}

/**
 * Issue 7 (live required-field indicator): true when a field is required and currently
 * blank, independent of whether the user has attempted to submit yet. This drives the
 * always-on red outline in Field.jsx, as opposed to `problem`, which still only shows
 * the detailed message text after a submit attempt (`showProblems`).
 *
 * `matterType.selection` (field.type === 'matterTypeSelect') is a special case: its own
 * path is never written to on older matters (only the two legacy booleans
 * matterType.isLitigation / isNonLitigation are), and even on new matters the value
 * passed in here is read from that same path. Treating it like any other field would
 * leave the red ring on forever once a radio is chosen on an old matter, or double up
 * with the structural check on new ones. `matter` is optional so existing callers/tests
 * that only care about ordinary fields are unaffected.
 */
export function isLiveIncomplete(field, value, matter) {
  if (field?.type === 'matterTypeSelect') {
    if (!field?.isRequired) return false;
    if (matter) {
      const isLit = getPath(matter, 'matterType.isLitigation') === true;
      const isNonLit = getPath(matter, 'matterType.isNonLitigation') === true;
      return !isLit && !isNonLit;
    }
    return isBlank(value);
  }
  return Boolean(field?.isRequired) && isBlank(value);
}

/**
 * Issue 7: a section counts as "complete" once none of its current problems are at the
 * REQUIRED level — i.e. every required field in it is filled in. Soft-required warnings
 * don't count against completeness since they're acknowledgeable rather than blocking.
 */
export function isSectionComplete(sectionProblems = []) {
  return !sectionProblems.some((p) => p.level === REQUIREMENT.REQUIRED);
}

/**
 * `sections` defaults to the code schema. The intake form passes the *effective* schema
 * instead (code defaults + `settings/fieldLayout` overrides — see lib/fieldLayout.js), so
 * hidden, reordered and admin-invented fields flow through unchanged.
 */
export function visibleSections(matter, sections = FORM_SECTIONS) {
  return sections
    .map((section) => ({
      ...section,
      fields: section.fields.filter((f) => isFieldVisible(f, matter)),
    }))
    .filter((section) => section.fields.length > 0 && isFieldVisible(section, matter));
}

/** The code schema, used whenever a caller does not supply an effective one. */
const BASE_SCHEMA = { sections: FORM_SECTIONS, allFields: ALL_FIELDS, roster: [], originationCapPercent: 75 };

/**
 * @param {object} rulesDoc  the `settings/fieldRules` document ({ version, rules }), or null
 *                           while it is still loading — in which case everything is optional
 *                           and submission is blocked by the caller instead.
 * @param {object} [schema]  { sections, allFields } — the effective schema to validate
 *                           against. Defaults to the code schema.
 */
export function makeRulesEngine(rulesDoc, schema = BASE_SCHEMA) {
  // Reason-quality thresholds ride on the same document as the requirement levels, since
  // they only mean anything for soft-required fields (lib/bypassReason.js).
  const bypassReasonRules = (rulesDoc && rulesDoc.bypassReasons) || null;
  const rules = (rulesDoc && rulesDoc.rules) || {};

  const requirementFor = (path) => rules[path]?.requirement || DEFAULT_REQUIREMENT;
  const helpFor = (path) => rules[path]?.helpText || '';
  /**
   * A disabled field is shown and greyed out rather than hidden. That is a different intent from
   * deleting it in the layout editor: the answer still belongs on the form and still prints, the
   * person filling it in simply is not the one who sets it — because a feed, an auto-fill rule or
   * firm policy does.
   *
   * `locked` is read as well as `disabled`: it is what the first version of this setting was
   * called, and reading both means an admin who already ticked the box does not silently lose it.
   */
  const disabledFor = (path) => rules[path]?.disabled === true || rules[path]?.locked === true;
  // A per-field layout override is the more specific, more recently-edited setting, so it
  // wins over settings/fieldRules without overwriting it.
  const requirementOf = (field) => field.requirementOverride || requirementFor(field.path);

  return {
    version: rulesDoc?.version ?? null,
    requirementFor,
    helpFor,

    disabledFor,

    /** Decorate a schema field with its live requirement + help text. */
    decorate(field) {
      const requirement = requirementOf(field);
      return {
        ...field,
        requirement,
        isRequired: requirement === REQUIREMENT.REQUIRED,
        isSoftRequired: requirement === REQUIREMENT.SOFT_REQUIRED,
        // Always-disabled. The CONDITIONAL half (`field.disabledIf`) is deliberately not resolved
        // here: decorate() has no matter to evaluate it against, and inventing one would be worse
        // than letting the control — which does have it — do that itself. See Field.jsx.
        isDisabled: field.disabledOverride ?? disabledFor(field.path),
        help: field.helpOverride || helpFor(field.path) || field.help || '',
      };
    },

    validate: (matter, bypasses = {}) =>
      validate(matter, { requirementOf, bypassReasonRules }, bypasses, schema),
    /** The reason-quality rules in force, for the panel that collects the reasons. */
    bypassReasonRules,
  };
}

/**
 * Cross-field checks that a per-path requirement level cannot express.
 * Kept small and explicit rather than inventing a second condition DSL.
 *
 * Every check is gated on its anchor field still being PRESENT in the effective schema
 * (`presentPaths` — hidden and admin-deleted fields are absent from it). An admin who
 * removes the Wall Builder or Matter Type from the form has removed that governance
 * question itself; keeping the structural check alive would block every submission with
 * an error pointing at a field that no longer renders anywhere. The deletion flow names
 * the disabled gate at delete time (conditionPaths.js STRUCTURAL_GATES — keep that map in
 * sync with the anchors used below); that is where the accountability for removing a
 * gate lives.
 */
function structuralProblems(matter, presentPaths, sectionIdByPath, roster, originationCapPercent) {
  const problems = [];
  const present = (path) => !presentPaths || presentPaths.has(path);
  /**
   * The page a structural problem belongs to, taken from the EFFECTIVE schema rather than
   * hardcoded. Admins move fields between pages (the live layout has Origination Split on
   * the intake-setup page and no 'origination' page at all), and problems are grouped by
   * sectionId — a stale id files the error under a page that no longer holds the field, so
   * that page's "complete" badge ignores it and the message surfaces nowhere useful. The
   * literal stays as the fallback for callers that pass no map.
   */
  const sectionFor = (path, fallback) =>
    (sectionIdByPath && sectionIdByPath.get(path)) || fallback;
  /** A person's name for a message, falling back to the raw uid rather than printing blank. */
  const nameOf = (uid) => {
    const person = (Array.isArray(roster) ? roster : []).find((u) => u.id === uid);
    return (person && (person.displayName || person.email)) || uid;
  };
  /** "Bob", "Bob and Jane", "Bob, Jane and Sam" — readable in a sentence. */
  const nameList = (uids) => {
    const names = uids.map(nameOf);
    if (names.length <= 1) return names.join('');
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  };

  // The revised paper workflow sends a "Hits Found" branch through an alert banner
  // before it may return to Matter Additional Details. The acknowledgement is therefore
  // a hard, same-actor submission gate rather than a soft-required bypass.
  if (
    present('conflictCheck.hitsAcknowledgedByAttorney') &&
    getPath(matter, 'conflictCheck.hitsFound') === true &&
    getPath(matter, 'conflictCheck.hitsAcknowledgedByAttorney') !== true
  ) {
    problems.push({
      path: 'conflictCheck.hitsAcknowledgedByAttorney',
      sectionId: sectionFor('conflictCheck.hitsAcknowledgedByAttorney', 'conflictCheck'),
      label: 'Conflict hits acknowledgement',
      message: 'Alert the Requesting Attorney to the conflict search results and confirm the acknowledgement before submitting.',
      level: REQUIREMENT.REQUIRED,
    });
  }

  // matterType.isLitigation / isNonLitigation are mutually exclusive (Issue 4) — the
  // MatterTypeSelect control in Field.jsx never lets both be true at once, so the only
  // remaining structural problem is neither being chosen yet.
  const isLit = getPath(matter, 'matterType.isLitigation') === true;
  const isNonLit = getPath(matter, 'matterType.isNonLitigation') === true;
  if (present('matterType.selection') && !isLit && !isNonLit) {
    problems.push({
      path: 'matterType.selection',
      sectionId: sectionFor('matterType.selection', 'matterType'),
      label: 'Matter type',
      message: 'Select Litigation or Non-Litigation.',
      level: REQUIREMENT.REQUIRED,
    });
  }

  // Origination split. Every rule lives in lib/origination.js so the CONTROL renders exactly
  // the same complaints it enforces — three implementations of "does this add up" would
  // eventually disagree, and the one that disagreed quietly would be the one that mattered.
  //
  // All hard errors, and all marked blocksNavigation: a split that does not add up is invalid
  // data rather than an unfinished answer, so it stops Next even where the firm has turned the
  // page gate off (see lib/navigationGate.js).
  if (present('origination.workingAttorneys')) {
    const rows = getPath(matter, 'origination.workingAttorneys') || [];
    const cap = firmOriginationCap({ originationCapPercent });
    for (const problem of originationProblems(rows, { capPercent: cap, nameOf })) {
      problems.push({
        path: 'origination.workingAttorneys',
        sectionId: sectionFor('origination.workingAttorneys', 'origination'),
        label: 'Origination split',
        message: problem.message,
        code: problem.code,
        level: REQUIREMENT.REQUIRED,
        blocksNavigation: true,
      });
    }
  }
  // Wall Builder (Issue 3): at least one practice group OR one person must be
  // designated. Both underlying paths are individually OPTIONAL (see
  // fieldRulesDefaults.js) since neither alone is required — only their combination.
  const wallGroups = getPath(matter, 'wallBuilder.practiceGroupIds') || [];
  const wallPeople = getPath(matter, 'wallBuilder.personUids') || [];
  if (
    (present('wallBuilder.practiceGroupIds') || present('wallBuilder.personUids')) &&
    wallGroups.length === 0 &&
    wallPeople.length === 0
  ) {
    problems.push({
      path: 'wallBuilder.practiceGroupIds',
      sectionId: sectionFor('wallBuilder.practiceGroupIds', 'wallBuilder'),
      label: 'Wall Builder Designation',
      message: 'Designate at least one practice group or person permitted access.',
      level: REQUIREMENT.REQUIRED,
    });
  }

  const areas = present('areasOfLaw') ? getPath(matter, 'areasOfLaw') : null;
  const hasOtherArea = Array.isArray(areas) ? areas.includes('other') : areas === 'other';
  if (hasOtherArea && present('areaOfLawOther') && isBlank(getPath(matter, 'areaOfLawOther'))) {
    problems.push({
      path: 'areaOfLawOther',
      sectionId: sectionFor('areasOfLaw', 'areasOfLaw'),
      label: 'Other area of law',
      message: 'Describe the "Other" area of law you selected.',
      level: REQUIREMENT.REQUIRED,
    });
  }
  if (
    hasOtherArea &&
    present('areaOfLawOtherReason') &&
    isBlank(getPath(matter, 'areaOfLawOtherReason'))
  ) {
    problems.push({
      path: 'areaOfLawOtherReason',
      sectionId: sectionFor('areasOfLaw', 'areasOfLaw'),
      label: 'Reason for selecting Other',
      message: 'Explain why this matter does not fit an existing area of law.',
      level: REQUIREMENT.REQUIRED,
    });
  }

  return problems;
}

/**
 * Range/precision check for a 'percent' field, or null when there's nothing wrong.
 * Blank is not a range problem — that's the requirement engine's job, not this one.
 */
function percentRangeProblem(field, value) {
  if (field.type !== 'percent' || isBlank(value)) return null;
  const { min, max, decimals } = percentParams(field);
  const problem = {
    path: field.path,
    sectionId: field.sectionId,
    sectionTitle: field.sectionTitle,
    label: field.label,
    level: REQUIREMENT.REQUIRED,
  };
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return { ...problem, message: `${field.label} must be a number.` };
  }
  if (number < min || number > max) {
    return { ...problem, message: `${field.label} must be between ${min}% and ${max}%.` };
  }
  // Count actual decimal places, so a stricter setting (e.g. whole numbers only) is
  // enforced rather than silently rounded behind the submitter's back.
  const places = (String(value).split('.')[1] || '').length;
  if (places > decimals) {
    return {
      ...problem,
      message:
        decimals === 0
          ? `${field.label} must be a whole number.`
          : `${field.label} allows at most ${decimals} decimal place${decimals === 1 ? '' : 's'}.`,
    };
  }
  return null;
}

function validate(matter, { requirementOf, bypassReasonRules }, bypasses, schema) {
  /**
   * A soft-required warning stops blocking only once a reason is given that actually says
   * something. Truthiness alone used to be enough, so "n/a" or "." bought a submission and
   * left an audit entry that looks like a considered waiver and is not one.
   */
  const waived = (path) => isAcceptedBypassReason(bypasses[path], bypassReasonRules);
  const errors = [];
  const warnings = [];
  /**
   * Every blank soft-required field, waived or not.
   *
   * `warnings` cannot serve this: a field drops out of it the instant its reason becomes
   * acceptable, which is correct for "what is still blocking submission" and disastrous as the
   * list the explain-yourself panel renders from. A reason crosses the 15-character threshold
   * MID-WORD, so the row — and the textarea being typed into — unmounted underneath the
   * person writing it. What got recorded was whatever fragment they had reached: one real
   * matter went to approval explaining "Because it is n".
   *
   * This list is stable while someone types. Whether a row still blocks is the `waived` flag
   * on it, not its presence.
   */
  const softRequired = [];

  // A field is only reachable if its section renders too. Checking field.visibleIf alone
  // would demand litigation answers on a non-litigation matter, which the form never
  // shows — an unresolvable submission.
  const openSections = new Set(visibleSections(matter, schema.sections).map((s) => s.id));

  for (const field of schema.allFields) {
    if (field.type === 'notice') continue;
    // matterTypeSelect is backed by two sibling booleans (matterType.isLitigation /
    // isNonLitigation), not directly by matterType.selection itself — structuralProblems()
    // below already enforces "one of the two booleans must be set" using those booleans.
    // Running the generic blank check against matterType.selection here would double-report
    // the same problem, and would permanently fail it for any older matter that has a
    // boolean set but was saved before `selection` existed — which is exactly the reported
    // bug (radio selected, error/red ring never clears).
    if (field.type === 'matterTypeSelect') continue;
    // A repeating group with no columns renders a "no columns yet" placeholder and no
    // Add button, so the submitter cannot create an entry. Demanding one would hard-block
    // every submission with an error only an administrator could clear.
    if (field.type === 'repeatable' && !(Array.isArray(field.fields) && field.fields.length)) {
      continue;
    }
    if (!openSections.has(field.sectionId)) continue;
    if (!isFieldVisible(field, matter)) continue;

    // A percentage outside its configured bounds is always a hard error, regardless of
    // requirement level: an optional field can be left blank, but a value that IS there
    // has to be in range. Checked before the blank/requirement gates below, which skip
    // optional fields and any field that already has a value.
    const rangeProblem = percentRangeProblem(field, getPath(matter, field.path));
    if (rangeProblem) errors.push(rangeProblem);

    // A half-typed address passes isBlank (something IS in there) while being useless to post
    // to — the same trap an origination split's placeholder rows fell into. Like the percent
    // range, this is a hard error whatever the requirement level: an optional address may be
    // empty, but one that has been started has to be finishable.
    if (field.type === 'address') {
      const problem = addressProblem(getPath(matter, field.path), { label: field.label });
      if (problem) {
        errors.push({
          path: field.path,
          sectionId: field.sectionId,
          sectionTitle: field.sectionTitle,
          label: field.label,
          message: problem.message,
          level: REQUIREMENT.REQUIRED,
        });
      }
    }

    const requirement = requirementOf(field);
    if (requirement === REQUIREMENT.OPTIONAL) continue;
    if (!isBlank(getPath(matter, field.path))) continue;

    const problem = {
      path: field.path,
      sectionId: field.sectionId,
      sectionTitle: field.sectionTitle,
      label: field.label,
      message: `${field.label} is required.`,
      level: requirement,
    };

    if (requirement === REQUIREMENT.REQUIRED) {
      errors.push(problem);
    } else {
      const soft = { ...problem, message: `${field.label} is usually provided.` };
      softRequired.push({ ...soft, waived: waived(field.path) });
      if (!waived(field.path)) warnings.push(soft);
    }
  }

  // Structural checks apply only while their anchor field is still part of the effective
  // schema — hidden/deleted fields are absent from allFields, so removing a field also
  // removes the cross-field gate that would otherwise block submission invisibly.
  const presentPaths = new Set(schema.allFields.map((f) => f.path).filter(Boolean));
  // Real page ids for the structural gates, from the effective schema — see sectionFor().
  const sectionIdByPath = new Map(
    schema.allFields.filter((f) => f.path && f.sectionId).map((f) => [f.path, f.sectionId])
  );
  for (const problem of structuralProblems(
    matter,
    presentPaths,
    sectionIdByPath,
    schema.roster,
    schema.originationCapPercent
  )) {
    if (problem.level === REQUIREMENT.REQUIRED) errors.push(problem);
    else {
      // Structural soft-required problems are waivable too, so they belong in the panel
      // list on the same terms as a plain blank field.
      softRequired.push({ ...problem, waived: waived(problem.path) });
      if (!waived(problem.path)) warnings.push(problem);
    }
  }

  return {
    errors,
    warnings,
    /** Every blank soft-required field, each flagged `waived`. See the declaration above. */
    softRequired,
    /** Hard errors always block. Soft-required fields block only until acknowledged. */
    canSubmit: errors.length === 0 && warnings.length === 0,
    bySection: groupBySection([...errors, ...warnings]),
  };
}

function groupBySection(problems) {
  return problems.reduce((acc, p) => {
    (acc[p.sectionId] = acc[p.sectionId] || []).push(p);
    return acc;
  }, {});
}

/**
 * Shape the acknowledgements for the `submitMatter` callable, which freezes them onto the
 * matter and writes them to the audit log.
 *
 * `labels` (path -> field label) is optional; when supplied, each waiver carries the label
 * the field had AT SUBMISSION. That is what the record PDF and the approver emails print, so
 * renaming a field later cannot retroactively change what a matter says was waived.
 */
export function toBypassPayload(bypasses, labels = {}) {
  return Object.entries(bypasses)
    .filter(([, reason]) => !isBlank(reason))
    .map(([fieldPath, reason]) => ({
      fieldPath,
      reason: String(reason).trim(),
      ...(labels[fieldPath] ? { label: labels[fieldPath] } : {}),
    }));
}

/** "A", "A and B", "A, B and C" — readable inside a sentence. */
function joinList(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The submit/navigation banner text, DERIVED from the current problem set rather than
 * latched when the button was pressed.
 *
 * The banner used to be a piece of state written at submit time, so it kept saying
 * "Please complete the highlighted required fields" after every problem had been fixed and
 * every section badge had cleared — with nothing highlighted to complete. It only went away
 * on the next submit, which then succeeded. Deriving it means it disappears the moment the
 * last problem does, and comes back if one returns.
 *
 * Naming the sections matters because the banner sits at the bottom of one page of a
 * multi-page form: "2 fields need attention in Litigation and Client Source & Risk
 * Screening" tells the submitter where to go, which "the highlighted fields" did not.
 *
 * @param {{errors: Array, warnings: Array}} validation
 * @param {(sectionId: string) => string} titleOf resolves a section id to its page title
 * @param {'submit'|'navigate'} intent
 * @returns {string} the banner text, or '' when nothing is blocking
 */
export function describeBlockingProblems(validation, titleOf, intent = 'submit') {
  const errors = validation?.errors || [];
  const warnings = validation?.warnings || [];
  const problems = errors.length ? errors : warnings;
  if (!problems.length) return '';

  // One field can raise several problems (an origination split can be short of 100% AND
  // over the per-attorney cap); the submitter is being pointed at fields, not messages.
  const fieldCount = new Set(problems.map((p) => p.path)).size;
  const titles = [];
  for (const problem of problems) {
    const title = titleOf(problem.sectionId) || problem.sectionTitle;
    if (title && !titles.includes(title)) titles.push(title);
  }

  const subject = `${fieldCount} field${fieldCount === 1 ? '' : 's'}`;
  const verb = fieldCount === 1 ? 'needs' : 'need';
  const where = titles.length ? ` in ${joinList(titles)}` : '';

  if (!errors.length) {
    return `${subject} ${verb} a reason before submitting${where}.`;
  }
  return intent === 'navigate'
    ? `${subject} ${verb} attention before continuing${where}.`
    : `${subject} ${verb} attention${where}.`;
}

/**
 * The banner text for a blocked submit or page-advance attempt, scoping and wording chosen
 * from the current problem set.
 *
 * This exists as a plain function rather than being computed in the component because the
 * component can only reach it BELOW its early returns (it needs the active section, which is
 * resolved after them). Written as a `useMemo` there, it changed the hook count between the
 * loading render and the loaded render and crashed the page with React error #310 — the exact
 * hazard IntakeForm.jsx already carried a comment about. Pure code cannot reintroduce it, and
 * it can be tested directly.
 *
 * A NAVIGATION gate is about the page it is blocking, so it describes only that page's
 * problems; a SUBMIT gate describes all of them.
 *
 * @param {{errors: Array, warnings: Array, canSubmit: boolean, bySection: Object}} validation
 * @param {{intent: 'submit'|'navigate'|null, sections: Array, sectionId: string}} options
 */
export function blockingBannerText(validation, { intent, sections = [], sectionId } = {}) {
  if (!intent || !validation || validation.canSubmit) return '';
  const titleOf = (id) => (sections.find((section) => section.id === id) || {}).title || '';

  if (intent === 'navigate') {
    const here = (validation.bySection && validation.bySection[sectionId]) || [];
    return describeBlockingProblems(
      {
        errors: here.filter((problem) => problem.level === REQUIREMENT.REQUIRED),
        warnings: here.filter((problem) => problem.level !== REQUIREMENT.REQUIRED),
      },
      titleOf,
      'navigate'
    );
  }
  return describeBlockingProblems(validation, titleOf, 'submit');
}
